import { useState, useRef, useEffect, useCallback } from "react";
import useIsMobile from "../hooks/useIsMobile";
import {
  trackWizardStarted,
  trackWizardStep,
  trackWizardStepCompleted,
  trackWizardCompleted,
  trackWizardAbandoned,
  trackCtaVisible,
  trackDownloadContract,
  trackModelPdfDownload,
  getEntrySource,
} from "../utils/analytics";

const ABANDON_TIMEOUT_MS = 5 * 60 * 1000;

function draftKey(contractType) {
  return `gen-contracte:draft:${contractType}`;
}

function loadDraft(contractType) {
  try {
    const raw = localStorage.getItem(draftKey(contractType));
    if (!raw) return null;
    const { data, step } = JSON.parse(raw);
    return { data, step };
  } catch { return null; }
}

function saveDraft(contractType, data, step) {
  try {
    localStorage.setItem(draftKey(contractType), JSON.stringify({ data, step }));
  } catch {}
}

function clearDraft(contractType) {
  try { localStorage.removeItem(draftKey(contractType)); } catch {}
}

export default function StepWizard({ config, contractType, modelPdf, onBack, onStepChange }) {
  const draft = useRef(loadDraft(contractType));
  const [data, setData] = useState(() => draft.current ? { ...config.defaultData, ...draft.current.data } : config.defaultData);
  const [currentStep, setCurrentStep] = useState(() => draft.current ? draft.current.step : 0);
  const [errors, setErrors] = useState({});
  const [showDraftBanner, setShowDraftBanner] = useState(() => !!draft.current);
  const contractRef = useRef(null);
  const pvRef = useRef(null);
  const ctaButtonRef = useRef(null);
  const stepEnteredAtRef = useRef(Date.now());
  const completedRef = useRef(false);
  const wizardStartedRef = useRef(false);
  const ctaSeenRef = useRef(false);
  const lastStepReachedRef = useRef(0);
  const finalStepTrackedRef = useRef(false);

  const steps = config.steps.filter((s) => !s.visible || s.visible(data));
  const active = Math.min(currentStep, steps.length - 1);
  const step = steps[active];

  const update = (field) => (e) => {
    setData((prev) => ({ ...prev, [field]: e.target ? e.target.value : e }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const toggleField = (field) => () =>
    setData((prev) => ({ ...prev, [field]: !prev[field] }));

  const setField = (field, value) =>
    setData((prev) => ({ ...prev, [field]: value }));

  const validateStep = () => {
    const newErrors = config.validate(step.id, data);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const stepName = step?.id;
  const pvEnabled = data.pvEnabled;
  // Scope of the wizard run, sent on every wizard event so funnels can be
  // segmented by whether the user is also generating the PV.
  const documentType = pvEnabled ? "contract_si_pv" : "contract";
  // Mirrored into a ref so the abandon effect can read the current value
  // without re-subscribing (and resetting its inactivity timer) on every toggle.
  const documentTypeRef = useRef(documentType);
  documentTypeRef.current = documentType;

  // Persist draft on every change so users can resume across sessions.
  useEffect(() => {
    saveDraft(contractType, data, active);
  }, [data, active, contractType]);

  // Step-change tracking: legacy wizard_step + wizard_started (once on Step 1).
  useEffect(() => {
    onStepChange?.(active);

    if (active + 1 > lastStepReachedRef.current) {
      lastStepReachedRef.current = active + 1;
    }

    if (!wizardStartedRef.current && active === 0) {
      wizardStartedRef.current = true;
      trackWizardStarted(contractType, getEntrySource());
    }

    trackWizardStep(contractType, active + 1, stepName, documentType);

    stepEnteredAtRef.current = Date.now();
  }, [active, onStepChange, contractType, stepName, documentType]);

  // Abandon detection: tab close OR ABANDON_TIMEOUT_MS of inactivity,
  // only fired once and only if the wizard wasn't completed.
  useEffect(() => {
    let timeoutId;
    let fired = false;

    const fireAbandon = (trigger) => {
      if (fired || completedRef.current || !wizardStartedRef.current) return;
      fired = true;
      trackWizardAbandoned(
        contractType,
        lastStepReachedRef.current,
        trigger,
        documentTypeRef.current,
      );
    };

    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fireAbandon("inactivity"), ABANDON_TIMEOUT_MS);
    };

    const onBeforeUnload = () => fireAbandon("beforeunload");
    const onActivity = () => resetTimer();

    resetTimer();
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("mousemove", onActivity, { passive: true });
    window.addEventListener("keydown", onActivity);
    window.addEventListener("click", onActivity);
    window.addEventListener("touchstart", onActivity, { passive: true });

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("keydown", onActivity);
      window.removeEventListener("click", onActivity);
      window.removeEventListener("touchstart", onActivity);
    };
  }, [contractType]);

  // CTA visibility: fires once per mount when the primary CTA enters the
  // viewport. Distinguishes "didn't see CTA" from "saw it, didn't click".
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const el = ctaButtonRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !ctaSeenRef.current) {
            ctaSeenRef.current = true;
            trackCtaVisible(contractType, active + 1);
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [active, contractType]);

  // Both final-step buttons (contract and PV) finish the wizard, so they share
  // one set of side-effects. wizard_step_completed is guarded to fire once even
  // when the user downloads both documents, and now also fires when the PV is
  // the only download.
  const completeFinalStep = useCallback(() => {
    completedRef.current = true;
    clearDraft(contractType);

    if (finalStepTrackedRef.current) return;
    finalStepTrackedRef.current = true;
    trackWizardStepCompleted(
      contractType,
      active + 1,
      stepName,
      Math.round((Date.now() - stepEnteredAtRef.current) / 1000),
      documentType,
    );
    // Terminal completion signal, independent of step_number, so funnels can
    // measure completion uniformly across contract types.
    trackWizardCompleted(contractType, documentType, active + 1);
  }, [contractType, active, stepName, documentType]);

  const handleDismissDraft = useCallback(() => {
    setShowDraftBanner(false);
  }, []);

  const handleRestart = useCallback(() => {
    setData(config.defaultData);
    setCurrentStep(0);
    setErrors({});
    setShowDraftBanner(false);
    clearDraft(contractType);
  }, [config.defaultData, contractType]);

  const isMobile = useIsMobile();
  const StepComponent = step.component;

  return (
    <div>
      {showDraftBanner && (
        <div style={{
          maxWidth: "800px",
          margin: "0 auto",
          padding: isMobile ? "16px 16px 0" : "16px 24px 0",
        }}>
          <div style={{
            display: "flex",
            alignItems: isMobile ? "flex-start" : "center",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "10px" : "16px",
            padding: "14px 18px",
            backgroundColor: "#fef9f0",
            border: "1px solid #e8d5b8",
            borderRadius: "10px",
            fontFamily: "'DM Sans', sans-serif",
          }}>
            <span style={{ fontSize: "14px", color: "#4a3f35", flex: 1 }}>
              💾 Am restaurat datele salvate anterior.
            </span>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={handleDismissDraft}
                style={{
                  padding: "6px 14px",
                  borderRadius: "6px",
                  border: "none",
                  background: "linear-gradient(135deg, #c45d3e 0%, #a84832 100%)",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Continuă
              </button>
              <button
                onClick={handleRestart}
                style={{
                  padding: "6px 14px",
                  borderRadius: "6px",
                  border: "1px solid #d4cdc0",
                  backgroundColor: "#fff",
                  color: "#6b6156",
                  fontSize: "13px",
                  fontWeight: 500,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Reîncepe
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Stepper */}
      <div style={{ maxWidth: "800px", margin: "0 auto", padding: isMobile ? "24px 16px 0" : "24px 24px 0" }}>
        {isMobile ? (
          <>
            <div style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "13px",
              fontWeight: 600,
              color: "#4a3f35",
              marginBottom: "8px",
            }}>
              Pasul {active + 1} din {steps.length} — {step.title || step.label}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ flex: 1, height: "6px", backgroundColor: "#e8e0d4", borderRadius: "3px", overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${((active + 1) / steps.length) * 100}%`,
                    background: "linear-gradient(135deg, #c45d3e 0%, #a84832 100%)",
                    borderRadius: "3px",
                    transition: "width 0.4s ease",
                  }}
                />
              </div>
              <span style={{ fontSize: "12px", color: "#8a7e70", fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap" }}>
                {Math.round(((active + 1) / steps.length) * 100)}%
              </span>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "flex", gap: "4px", marginBottom: "8px" }}>
              {steps.map((s, i) => (
                <div
                  key={s.id}
                  onClick={() => {
                    if (i < active || validateStep()) {
                      setErrors({});
                      setCurrentStep(i);
                    }
                  }}
                  style={{
                    flex: 1,
                    cursor: "pointer",
                    textAlign: "center",
                    padding: "12px 4px",
                    borderRadius: "10px",
                    backgroundColor: i === active ? "#fff" : "transparent",
                    boxShadow: i === active ? "0 2px 12px rgba(0,0,0,0.08)" : "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div style={{ fontSize: "20px", marginBottom: "4px" }}>{s.icon}</div>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: i === active ? 700 : 500,
                      color: i === active ? "#c45d3e" : i < active ? "#4a3f35" : "#b8a890",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Progress bar + auto-save indicator (desktop) */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", fontSize: "12px", color: "#8a7e70", fontFamily: "'DM Sans', sans-serif", marginBottom: "6px", marginTop: "4px" }}>
              <span style={{ fontSize: "13px" }}>💾</span>
              Salvăm progresul automat
            </div>
            <div style={{ height: "3px", backgroundColor: "#e8e0d4", borderRadius: "2px", overflow: "hidden" }}>
              <div
                style={{
                  height: "100%",
                  width: `${((active + 1) / steps.length) * 100}%`,
                  backgroundColor: "#c45d3e",
                  borderRadius: "2px",
                  transition: "width 0.4s ease",
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* Content */}
      <div style={{ maxWidth: "800px", margin: "0 auto", padding: isMobile ? "24px 16px" : "24px", paddingBottom: isMobile && active < steps.length - 1 ? "100px" : undefined }}>
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            padding: isMobile ? "16px" : "32px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            border: "1px solid #e8e0d4",
          }}
        >
          <h2
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "22px",
              fontWeight: 700,
              color: "#2d2418",
              margin: "0 0 8px 0",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <span style={{ fontSize: "28px" }}>{step.icon}</span>
            {step.title || step.label}
          </h2>

          <StepComponent
            data={data}
            errors={errors}
            update={update}
            toggleField={toggleField}
            setField={setField}
            contractRef={contractRef}
            pvRef={pvRef}
          />
        </div>

        {/* Step 1 escape hatch: PDF-intent visitors (esp. organic search) who
            hit the identity form can grab the blank PDF instead of leaving.
            Distinct source ("wizard" / "step1") so we can measure whether it
            recovers would-be abandoners. Only shown when a blank PDF exists. */}
        {active === 0 && modelPdf && (
          <div style={{ textAlign: "center", marginTop: "16px" }}>
            <a
              href={`/models/${modelPdf}`}
              download={modelPdf}
              onClick={() => trackModelPdfDownload(contractType, "wizard", "step1")}
              style={{
                fontSize: "13px",
                color: "#8a7e70",
                textDecoration: "none",
                borderBottom: "1px dotted #b8a890",
                paddingBottom: "1px",
              }}
            >
              Preferi să completezi de mână? Descarcă modelul PDF gol →
            </a>
          </div>
        )}

        {/* Navigation */}
        <div style={isMobile && active < steps.length - 1 ? {
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "#fff",
          padding: "12px 16px",
          borderTop: "1px solid #e8e0d4",
          boxShadow: "0 -2px 12px rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          zIndex: 50,
        } : { display: "flex", justifyContent: "space-between", marginTop: "24px", gap: "12px" }}>
          <button
            onClick={() => {
              if (active === 0 && onBack) {
                onBack();
              } else {
                setErrors({});
                setCurrentStep(Math.max(0, active - 1));
              }
            }}
            style={{
              padding: "8px 0",
              border: "none",
              backgroundColor: "transparent",
              color: "#8a7e70",
              fontSize: "14px",
              fontWeight: 500,
              fontFamily: "'DM Sans', sans-serif",
              cursor: "pointer",
              transition: "color 0.2s ease",
              whiteSpace: "nowrap",
            }}
          >
            {active === 0 && onBack ? "← Acasa" : "← Inapoi"}
          </button>

          {active < steps.length - 1 ? (
            <button
              ref={ctaButtonRef}
              onClick={() => {
                if (validateStep()) {
                  trackWizardStepCompleted(
                    contractType,
                    active + 1,
                    stepName,
                    Math.round((Date.now() - stepEnteredAtRef.current) / 1000),
                    documentType,
                  );
                  setCurrentStep(active + 1);
                }
              }}
              style={{
                padding: isMobile ? "14px 28px" : "14px 28px",
                borderRadius: "12px",
                border: "none",
                background: "linear-gradient(135deg, #c45d3e 0%, #a84832 100%)",
                color: "#fff",
                fontSize: "15px",
                fontWeight: 600,
                fontFamily: "'DM Sans', sans-serif",
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(196, 93, 62, 0.3)",
                transition: "all 0.2s ease",
                flex: isMobile ? 1 : undefined,
                marginLeft: isMobile ? "12px" : undefined,
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 6px 20px rgba(196, 93, 62, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 14px rgba(196, 93, 62, 0.3)";
              }}
            >
              {active === 0 ? "Generează Gratuit →" : "Continua →"}
            </button>
          ) : (
            <div style={{ display: "flex", gap: "12px", flexDirection: isMobile ? "column" : "row" }}>
              <button
                ref={ctaButtonRef}
                onClick={() => {
                  config.buildPDF(data, config.pdfFilename || "contract.pdf");
                  completeFinalStep();
                  trackDownloadContract(contractType, "contract");
                }}
                style={{
                  padding: isMobile ? "12px 20px" : "14px 32px",
                  width: isMobile ? "100%" : "auto",
                  borderRadius: "12px",
                  border: "none",
                  background: "linear-gradient(135deg, #2d7a4f 0%, #1d5e3a 100%)",
                  color: "#fff",
                  fontSize: "15px",
                  fontWeight: 600,
                  fontFamily: "'DM Sans', sans-serif",
                  cursor: "pointer",
                  boxShadow: "0 4px 14px rgba(45, 122, 79, 0.3)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 6px 20px rgba(45, 122, 79, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 4px 14px rgba(45, 122, 79, 0.3)";
                }}
              >
                {config.downloadLabel || "Descarca Contractul"}
              </button>
              {data.pvEnabled && (
                <button
                  onClick={() => {
                    config.buildPVPDF(data, "proces-verbal-predare-primire.pdf");
                    completeFinalStep();
                    trackDownloadContract(contractType, "proces_verbal");
                  }}
                  style={{
                    padding: isMobile ? "12px 20px" : "14px 32px",
                    width: isMobile ? "100%" : "auto",
                    borderRadius: "12px",
                    border: "none",
                    background: "linear-gradient(135deg, #3a5ba0 0%, #2a4580 100%)",
                    color: "#fff",
                    fontSize: "15px",
                    fontWeight: 600,
                    fontFamily: "'DM Sans', sans-serif",
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(58, 91, 160, 0.3)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow = "0 6px 20px rgba(58, 91, 160, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 4px 14px rgba(58, 91, 160, 0.3)";
                  }}
                >
                  Descarca Proces Verbal
                </button>
              )}
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <div
          style={{
            marginTop: "32px",
            padding: "20px 24px",
            backgroundColor: "rgba(196, 93, 62, 0.06)",
            borderRadius: "12px",
            border: "1px solid rgba(196, 93, 62, 0.12)",
          }}
        >
          <p style={{ fontSize: "13px", color: "#8a7e70", margin: 0, lineHeight: 1.6 }}>
            <strong style={{ color: "#4a3f35" }}>Disclaimer:</strong> Documentul generat este un model orientativ și nu constituie consultanță juridică. Utilizarea acestuia se face pe propria răspundere. Pentru situații particulare sau complexe, se recomandă consultarea unui avocat. Utilizatorul este responsabil pentru verificarea, adaptarea documentului și respectarea obligațiilor legale aplicabile.
          </p>
        </div>

      </div>
    </div>
  );
}
